import {
  convertFile,
  convertFilesAndFolders,
  convertResourceAcknowledgements,
  File,
  FilesAndFolders,
  Id,
  RepositoryResponse,
  ResourceAcknowledgement,
} from '@nextforce/api'
import { useQuery } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import keys from './shared/keys'

export function useChatGroupFileFolders(conversationId: Id, folderId?: Id) {
  const { server } = useServer()
  return useQuery<FilesAndFolders>({
    queryKey: keys.chatGroupFolder(conversationId, folderId),
    queryFn: fetchChatGroupFileFolders,
  })

  async function fetchChatGroupFileFolders() {
    let url = `/v2/chat/repository/${conversationId}/folders`
    if (folderId) {
      url += `?parent_folder_id=${folderId}`
    }
    const response = await server.get<RepositoryResponse.FilesAndFolders>(url)
    return convertFilesAndFolders(response)
  }
}

export function useChatGroupAllFileFolders(conversationId: Id) {
  const { server } = useServer()
  return useQuery<FilesAndFolders>({
    queryKey: keys.allFileFolders(conversationId),
    queryFn: fetchAllChatGroupFiles,
    enabled: !!conversationId,
  })

  async function fetchAllChatGroupFiles(): Promise<FilesAndFolders> {
    const response = await server.get<RepositoryResponse.FilesAndFolders>(
      `/v2/chat/repository/${conversationId}/all`
    )
    return convertFilesAndFolders(response)
  }
}

export function useSharedChatGroupFileFolders(
  conversationId: Id,
  folderId?: Id
) {
  const { server } = useServer()
  return useQuery<FilesAndFolders>({
    queryKey: keys.sharedChatGroupFolder(conversationId, folderId),
    queryFn: fetchSharedChatGroupFileFolders,
  })

  async function fetchSharedChatGroupFileFolders() {
    let url = `/v2/chat/repository/${conversationId}/shared`
    if (folderId) {
      url += `?parent_folder_id=${folderId}`
    }
    const response = await server.get<RepositoryResponse.FilesAndFolders>(url)
    return convertFilesAndFolders(response)
  }
}

export function useFilesWithAcknowledgementList(conversationId: Id) {
  const { server } = useServer()
  return useQuery<File[]>({
    queryKey: keys.filesWithAcknowledgementList(conversationId),
    queryFn: fetchFilesWithAcknowledgementList,
  })

  async function fetchFilesWithAcknowledgementList() {
    const response = await server.get<RepositoryResponse.File[]>(
      `/v2/chat/repository/${conversationId}/files/acknowledgement_list`
    )
    return response.map(convertFile)
  }
}

export function useFileAcknowledgements(fileId: Id) {
  const { server } = useServer()
  return useQuery<ResourceAcknowledgement[]>({
    queryKey: keys.fileAcknowledgements(fileId),
    queryFn: fetchFileAcknowledgements,
  })

  async function fetchFileAcknowledgements() {
    const response = await server.get<
      RepositoryResponse.ResourceAcknowledgement[]
    >(`/v2/chat/repository/files/${fileId}/acknowledgements`)
    return convertResourceAcknowledgements(response)
  }
}

export function useFileAcknowledgementUserIds(fileId: Id) {
  const { server } = useServer()

  return useQuery({
    queryKey: keys.fileAcknowledgementUserIds(fileId),
    queryFn: fetchFileAcknowledgementUserIds,
    enabled: !!fileId,
  })

  async function fetchFileAcknowledgementUserIds() {
    const response = await server.get<number[]>(
      `/v2/chat/repository/files/${fileId}/acknowledgement_user_ids`
    )
    return response
  }
}
